import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  MARITAL_STATUS_OPTIONS,
  EDUCATION_LEVELS,
  PROFESSION_OPTIONS,
  COMPLEXION_OPTIONS,
  HEIGHT_OPTIONS,
  CASTE_OPTIONS,
  RELIGION_OPTIONS,
  FOOD_HABITS_OPTIONS,
  BIRTH_STAR_OPTIONS,
  BIRTH_SIGN_OPTIONS,
} from '../constants/profileOptions';

const PreferencesTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  const scrollViewRef = useRef(null);
  const fieldPositions = useRef({});
  
  const [preferencesData, setPreferencesData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadPreferencesData();
  }, [memberId]);

  useEffect(() => {
    if (isEditMode && onEditStateChange && Object.keys(editData).length > 0) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: true,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  }, [editData, isEditMode]);

  const loadPreferencesData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/preferences`);
      const data = response.data?.data || response.data;
      // Always set data even if null/empty to allow editing
      setPreferencesData(data || {});
    } catch (error) {
      console.error('Error loading preferences data:', error);
      // Set empty object to allow editing even on error
      setPreferencesData({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    const pref = preferencesData?.partnerPreferences || preferencesData || {};
    setEditData(pref);
    setIsEditMode(true);
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: false,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    setValidationErrors({});
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: false,
        hasUnsavedChanges: false,
        onSave: null,
        onCancel: null,
      });
    }
  };

  const getMandatoryFields = () => [
    { key: 'maritalStatus', label: 'Marital Status' },
    { key: 'educationalQualification', label: 'Educational Qualification' },
  ];

  const handleSave = async () => {
    // Validate mandatory fields
    const mandatoryFields = getMandatoryFields();
    const errors = {};

    mandatoryFields.forEach(field => {
      const value = field.key in editData ? editData[field.key] : (preferencesData?.partnerPreferences?.[field.key] || preferencesData?.[field.key]);
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors[field.key] = `${field.label} is required`;
      }
    });

    // Validate maritalStatus - prevent empty placeholder from being saved
    const maritalStatusValue = editData.maritalStatus || (preferencesData?.partnerPreferences?.maritalStatus || preferencesData?.maritalStatus);
    if (maritalStatusValue) {
      const maritalStatusValues = typeof maritalStatusValue === 'string' 
        ? maritalStatusValue.split(',').map(v => v.trim()) 
        : (Array.isArray(maritalStatusValue) ? maritalStatusValue : [maritalStatusValue]);
      
      // Check if any value is empty string (the placeholder)
      const hasEmptyPlaceholder = maritalStatusValues.some(v => v === '' || v === 'Select Marital Status');
      if (hasEmptyPlaceholder) {
        errors.maritalStatus = 'Please select a valid Marital Status option';
      }
    }

    // Validate age range: minimum age must be less than or equal to maximum age
    const minAge = editData.ageGroupFrom || preferencesData?.partnerPreferences?.ageGroupFrom || preferencesData?.ageGroupFrom;
    const maxAge = editData.ageGroupTo || preferencesData?.partnerPreferences?.ageGroupTo || preferencesData?.ageGroupTo;
    
    if (minAge && maxAge) {
      const minAgeNum = parseInt(minAge, 10);
      const maxAgeNum = parseInt(maxAge, 10);
      
      if (!isNaN(minAgeNum) && !isNaN(maxAgeNum) && minAgeNum > maxAgeNum) {
        errors.ageGroupTo = 'Maximum age must be greater than minimum age';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Scroll to first error field
      if (scrollViewRef.current) {
        const firstErrorKey = Object.keys(errors)[0];
        const errorPosition = fieldPositions.current[firstErrorKey];
        if (errorPosition !== undefined) {
          scrollViewRef.current.scrollTo({ y: Math.max(0, errorPosition - 100), animated: true });
        } else {
          scrollViewRef.current.scrollTo({ y: 0, animated: true });
        }
      }
      
      Alert.alert('Validation Error', Object.values(errors)[0] || 'Please fill in all mandatory fields');
      return false;
    }

    setIsSaving(true);
    try {
      const response = await api.put(`/api/EnhancedMember/${memberId}/preferences`, editData);
      setPreferencesData(response.data?.data || response.data);
      Alert.alert('Success', 'Preferences updated successfully');
      setIsEditMode(false);
      setEditData({});
      setValidationErrors({});
      if (onEditStateChange) {
        onEditStateChange({
          isEditing: false,
          hasUnsavedChanges: false,
          onSave: null,
          onCancel: null,
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving preferences data:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to save changes');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const getOptionsForField = (fieldKey) => {
    const optionsMap = {
      maritalStatus: MARITAL_STATUS_OPTIONS.filter(opt => opt.value !== ''), // Remove empty placeholder
      educationalQualification: EDUCATION_LEVELS.filter(opt => opt.value !== ''), // Remove empty placeholder
      profession: PROFESSION_OPTIONS,
      complexion: COMPLEXION_OPTIONS,
      heightRangeFrom: HEIGHT_OPTIONS,
      heightRangeTo: HEIGHT_OPTIONS,
      caste: CASTE_OPTIONS,
      religion: RELIGION_OPTIONS,
      foodHabits: FOOD_HABITS_OPTIONS,
      birthStar: BIRTH_STAR_OPTIONS,
      birthSign: BIRTH_SIGN_OPTIONS,
    };
    return optionsMap[fieldKey] || [];
  };

  const openDropdown = (fieldKey, label) => {
    setActiveDropdownField({ key: fieldKey, label });
    setActiveDropdownOptions(getOptionsForField(fieldKey));
    setDropdownVisible(true);
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      // Special handling for maritalStatus, complexion, and educationalQualification (multiselection)
      if (activeDropdownField.key === 'maritalStatus' || activeDropdownField.key === 'complexion' || activeDropdownField.key === 'educationalQualification') {
        const currentValues = editData[activeDropdownField.key] 
          ? (Array.isArray(editData[activeDropdownField.key]) 
              ? editData[activeDropdownField.key] 
              : editData[activeDropdownField.key].split(',').map(v => v.trim()))
          : [];
        
        let newValues;
        if (value === 'Any') {
          // If "Any" is selected, clear all other selections and set only "Any"
          newValues = ['Any'];
        } else {
          // If another option is selected
          if (currentValues.includes(value)) {
            // Deselect if already selected
            newValues = currentValues.filter(v => v !== value);
          } else {
            // Add to selection and remove "Any" if present
            newValues = [...currentValues.filter(v => v !== 'Any'), value];
          }
        }
        
        // Store as comma-separated string
        setEditData({
          ...editData,
          [activeDropdownField.key]: newValues.length > 0 ? newValues.join(', ') : ''
        });
        // Clear error when user selects a value
        if (validationErrors[activeDropdownField.key]) {
          setValidationErrors({ ...validationErrors, [activeDropdownField.key]: undefined });
        }
        // Don't close modal for multiselection
        return;
      }
      
      // Default single selection for other fields
      // Convert height values to strings for API compatibility
      const finalValue = (activeDropdownField.key === 'heightRangeFrom' || activeDropdownField.key === 'heightRangeTo')
        ? String(value)
        : value;
      
      setEditData({
        ...editData,
        [activeDropdownField.key]: finalValue
      });
      // Clear error when user selects a value
      if (validationErrors[activeDropdownField.key]) {
        setValidationErrors({ ...validationErrors, [activeDropdownField.key]: undefined });
      }
    }
    setDropdownVisible(false);
  };

  // Helper function to get display label for dropdown values
  const getDisplayLabel = (fieldKey, value) => {
    if (!value && value !== 0) return null;
    
    const options = getOptionsForField(fieldKey);
    if (!options || options.length === 0) return value;
    
    // Handle multiselect fields (comma-separated values)
    if ((fieldKey === 'maritalStatus' || fieldKey === 'complexion' || fieldKey === 'educationalQualification') && typeof value === 'string' && value.includes(',')) {
      const values = value.split(',').map(v => v.trim());
      const labels = values.map(v => {
        const option = options.find(opt => opt.value == v);
        return option ? option.label : v;
      });
      return labels.join(', ');
    }
    
    // Handle single select fields (including height)
    const option = options.find(opt => opt.value == value); // Use == to handle string/number comparison
    return option ? option.label : value;
  };

  const renderSectionTitle = (title) => (
    <Text style={profileDetailStyles.sectionHeader}>{title}</Text>
  );

  const renderInfoRow = (label, value, fieldKey, editable = true, useDropdown = false) => {
    const isEditing = isEditMode && editable;
    const pref = preferencesData?.partnerPreferences || preferencesData;
    const isMandatory = getMandatoryFields().some(f => f.key === fieldKey);
    const hasError = validationErrors[fieldKey];
    const displayLabel = isEditing && isMandatory ? `${label} *` : label;
    
    if (!isEditing) {
      return (
        <View style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <View style={profileDetailStyles.viewModeContainer}>
              <Text style={profileDetailStyles.viewModeText}>{getDisplayLabel(fieldKey, value) || 'Not Provided'}</Text>
            </View>
          </View>
        </View>
      );
    }

    // Edit mode with dropdown
    if (useDropdown) {
      const displayValue = fieldKey in editData ? editData[fieldKey] : value;
      const displayLabelValue = getDisplayLabel(fieldKey, displayValue);
      
      return (
        <View 
          style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}
          onLayout={(event) => {
            fieldPositions.current[fieldKey] = event.nativeEvent.layout.y;
          }}
        >
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{displayLabel}</Text>
            <TouchableOpacity 
              style={[profileDetailStyles.dropdown, hasError && profileDetailStyles.inputError]}
              onPress={() => openDropdown(fieldKey, label)}
            >
              <Text style={displayValue ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {displayLabelValue || `Select ${label}`}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
            {hasError && (
              <Text style={profileDetailStyles.errorText}>{hasError}</Text>
            )}
          </View>
        </View>
      );
    }

    // Edit mode with text input (for ranges and free text)
    // Determine if numeric keyboard should be used
    const isNumericField = fieldKey === 'ageGroupFrom' || fieldKey === 'ageGroupTo';
    
    return (
      <View 
        style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}
        onLayout={(event) => {
          fieldPositions.current[fieldKey] = event.nativeEvent.layout.y;
        }}
      >
        <View style={profileDetailStyles.inputWrapper}>
          <Text style={profileDetailStyles.floatingLabel}>{displayLabel}</Text>
          <TextInput
            style={[
              profileDetailStyles.input,
              hasError && profileDetailStyles.inputError
            ]}
            value={fieldKey in editData ? (editData[fieldKey]?.toString() || '') : (value?.toString() || '')}
            onChangeText={(text) => {
              setEditData({ ...editData, [fieldKey]: text });
              if (hasError) {
                setValidationErrors({ ...validationErrors, [fieldKey]: undefined });
              }
            }}
            placeholder={`Enter ${label}`}
            placeholderTextColor={colors.textSecondary}
            keyboardType={isNumericField ? 'numeric' : 'default'}
          />
          {hasError && (
            <Text style={profileDetailStyles.errorText}>{hasError}</Text>
          )}
        </View>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={profileDetailStyles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={profileDetailStyles.loadingText}>Loading preferences...</Text>
      </View>
    );
  }

  if (!preferencesData) {
    return (
      <View style={profileDetailStyles.container}>
        <Text style={profileDetailStyles.noDataText}>No partner preferences data available</Text>
      </View>
    );
  }

  const pref = preferencesData.partnerPreferences || preferencesData;

  return (
    <View style={profileDetailStyles.container}>
      <ScrollView 
        ref={scrollViewRef}
        style={profileDetailStyles.scrollView} 
        contentContainerStyle={profileDetailStyles.scrollContent}
      >
        {renderSectionTitle('Basic Preferences')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Marital Status', pref.maritalStatus, 'maritalStatus', true, true)}
          {renderInfoRow('Educational Qualification', pref.educationalQualification, 'educationalQualification', true, true)}
          {renderInfoRow('Profession', pref.profession, 'profession', true, false)}
          {renderInfoRow('Complexion', pref.complexion, 'complexion', true, true)}
        </View>

        {renderSectionTitle('Physical & Age Preferences')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Minimum Height (cm)', pref.heightRangeFrom, 'heightRangeFrom', true, true)}
          {renderInfoRow('Maximum Height (cm)', pref.heightRangeTo, 'heightRangeTo', true, true)}
          {renderInfoRow('Minimum Age', pref.ageGroupFrom, 'ageGroupFrom')}
          {renderInfoRow('Maximum Age', pref.ageGroupTo, 'ageGroupTo')}
        </View>

        {renderSectionTitle('Cultural Preferences')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Religion', pref.religion, 'religion', true, true)}
          {renderInfoRow('Caste (Kulam)', pref.caste, 'caste', true, false)}
          {renderInfoRow('Birth Star (Nakshtram)', pref.birthStar, 'birthStar', true, true)}
          {renderInfoRow('Birth Sign (Raasi)', pref.birthSign, 'birthSign', true, true)}
        </View>

        {renderSectionTitle('Location & Lifestyle')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Current Location (Job Location)', pref.currentLocation, 'currentLocation')}
          {renderInfoRow('Food Habits', pref.foodHabits, 'foodHabits', true, true)}
        </View>
      </ScrollView>

      {/* Floating Edit Buttons */}
      {(isOwnProfile || isMediatorProfile) && (
        <View style={profileDetailStyles.floatingButtonContainer}>
          {isEditMode ? (
            <>
              <TouchableOpacity 
                onPress={handleCancelEdit} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingCancelButton]}
                disabled={isSaving}
              >
                <SvgIcon name="close" size={16} color={colors.white} />
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={handleSave} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingSaveButton]}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <SvgIcon name="checkmark" size={16} color={colors.white} />
                )}
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity 
              onPress={handleEdit} 
              style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingEditButton]}
            >
              <SvgIcon name="create-outline" size={16} color={colors.white} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity 
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <View style={profileDetailStyles.modalHeader}>
              <Text style={profileDetailStyles.modalTitle}>
                {activeDropdownField?.label || 'Select'}
              </Text>
              <TouchableOpacity onPress={() => setDropdownVisible(false)}>
                <SvgIcon name="close" size={24} color={colors.primary} />
              </TouchableOpacity>
            </View>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => {
                // Check if this option is selected (handle both single and multi-selection)
                let isSelected = false;
                if (activeDropdownField?.key === 'maritalStatus' || activeDropdownField?.key === 'complexion' || activeDropdownField?.key === 'educationalQualification') {
                  const currentValues = editData[activeDropdownField.key] 
                    ? (Array.isArray(editData[activeDropdownField.key]) 
                        ? editData[activeDropdownField.key] 
                        : editData[activeDropdownField.key].split(',').map(v => v.trim()))
                    : [];
                  isSelected = currentValues.includes(option.value);
                } else {
                  isSelected = (editData[activeDropdownField?.key] || pref?.[activeDropdownField?.key]) === option.value;
                }
                
                return (
                  <TouchableOpacity
                    key={option.value}
                    style={profileDetailStyles.modalItem}
                    onPress={() => selectDropdownOption(option.value)}
                  >
                    <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                    {isSelected && (
                      <SvgIcon name="checkmark-circle" size={20} color={colors.accent} />
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            {(activeDropdownField?.key === 'maritalStatus' || activeDropdownField?.key === 'complexion' || activeDropdownField?.key === 'educationalQualification') && (
              <TouchableOpacity 
                style={profileDetailStyles.modalDoneButton}
                onPress={() => setDropdownVisible(false)}
              >
                <Text style={profileDetailStyles.modalDoneButtonText}>Done</Text>
              </TouchableOpacity>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default PreferencesTabScreen;
